/**
 * 站外广告H5页面的登录逻辑封装，逐步代替autoLogin.js，提供如下能力：
 *
 * 2024.08.17修改H5兜底登录逻辑
 * 原：H5页面内弹登录框
 * 新：判断是否点评域名，如果是点评域名，跳转点评登录页，否则打开H5登录框
 *
 * 1、【用户手动触发】区分容器走不同的登录分支
 *      第一种：美团App || 外卖App，调用KNB的login (2024.08.17，增加点评App)
 *      第二种：美团小程序 || 外卖小程序，调用小程序重定向，增加参数wxapp_force_login=1，触发小程序登录，登录成功后，小程序会在url增加参数wxapp_login_status=0
 *      第三种：微信浏览器，跳转到团App，打开该页面，同时H5兜底登录逻辑 (2024.08.17，增加点评App)
 *      第四种：H5兜底，显示页面内登录弹框（同时会通过window.location.href打开美团App scheme）(2024.08.17，增加跳转点评App scheme，同时页面内跳转点评登录页)
 * 2、【页面加载完自动触发】
 *      第一种：
 *          团系容器（团App、外卖App），自动将url中的token写入cookie (2024.08.17，点评自动加cookie dper，前端无需兼容)
 *          这样在请求接口时，后端会自动拿到用户的登录状态
 *          参数名：wm_logintoken || token || mt_c_token
 *      第二种：
 *          团系小程序（团、外卖），自动将url中的token写入cookie
 *          这样在请求接口时，后端会自动拿到用户的登录状态
 *          参数解释：
 *          wxapp_force_login=1 从登录url进来
 *          wxapp_login_status=0 登录状态成功
 *          wm_logintoken 登录token
 *
 * 3、【用户手动触发】切换账户
 *      第一种：美团App || 外卖App，调用KNB的logout (2024.08.17，增加点评App)
 *      其他：弹出H5登录框  (2024.08.17，增加跳转点评登出页)
 */
import { container, createCookie, common, createDeepLink } from '@mtfe/sweet-utils';
import { addLoginError, addLoginSuccess, addTriggerLogin, getErrorMessage, LoginSource } from '@mtfe/sweet-report';

import oadWX from './oadWX';
import loadKNB from './loadKNB';

const {
    isInWeixin,
    isMiniProgram,
    isMiniProgramTuan,
    isMiniProgramWaimai,
    isInTuanApp,
    isInWaiMaiApp,
    isInDianPingApp,
    isMiniProgramDianping,
    isDianPingDomain,
    is51PingDomain,
} = container;

const Cookies = createCookie();
const { qsParse } = common;

const deeplink = createDeepLink();

const TOKEN_KEY = 'mt_c_token';

const getDpLoginUrl = () => `https://m.dianping.com/login?redir=${encodeURIComponent(window.location.href)}`;
const getDpLogoutUrl = () => `https://m.dianping.com/logout?redir=${encodeURIComponent(encodeURIComponent(window.location.href))}`;

const get51PingLoginUrl = () => `https://m.51ping.com/login?redir=${encodeURIComponent(window.location.href)}`;
const get51PingLogoutUrl = () => `https://m.51ping.com/logout?redir=${encodeURIComponent(encodeURIComponent(window.location.href))}`;

/**
 * 调用H5登录框
 * @param {*} onLoginSuccess
 * @param {*} showH5LoginModal
 */
const _useH5LoginModalFinally = (onLoginSuccess, showH5LoginModal) => {
    addTriggerLogin(LoginSource.h5, 'h5触发登录');
    const ping51LoginUrl = get51PingLoginUrl();
    const dianpingLoginUrl = getDpLoginUrl();
    if (isDianPingDomain) {
        window.location.href = dianpingLoginUrl;
    } else if (is51PingDomain) {
        window.location.href = ping51LoginUrl;
    } else {
        showH5LoginModal({
            loginSuccessCb: () => {
                addLoginSuccess(LoginSource.h5);
                setTimeout(() => {
                    onLoginSuccess && onLoginSuccess();
                }, 500);
            },
            loginFailCb: () => {
                addLoginError(LoginSource.h5, 'h5登录失败');
            },
        });
    }
};

/**
 * 调用客户端退出
 * https://km.sankuai.com/collabpage/419337910
 * 能否退登由账号后端决定（理想态：token 有效不允许退登；token 无效才可以退登）
 * @returns
 */
const _callClientLogout = () => {
    return new Promise((resolve, reject) => {
        KNB.ready(() => {
            KNB.logout({
                reason: 10000, // 退出理由，默认为 20000，表示被动退出，如果为 10000，表示主动退出，仅美团 10.3.200 以上支持
                success: function (data) {
                    Cookies.remove(TOKEN_KEY);
                    resolve(true);
                },
                fail: function (error) {
                    reject(error);
                },
            });
        });
    });
};

// 1、【用户触发】区分容器走不同的登录逻辑
/**
 * 
 * @param {*} props 样例：  
 * function showToast(content) {
 *     Message.info({ content });
 * }

 * function showH5LoginModal(loginSuccessCb, loginFailCb) {
 *     PRLoginModal.showLoginModal({
 *         loadLogin, // 外部UI组件参数，无需出入到 oadLogin
 *         title, // 外部UI组件参数，无需出入到 oadLogin
 *         loginSuccessCb,
 *         loginFailCb,
 *     });
 * }
 */
const oadLogin = async (props) => {
    const { onLoginSuccess, showToast = () => {}, showH5LoginModal = () => {} } = props;

    if (isInTuanApp || isInWaiMaiApp || isInDianPingApp) {
        // owl 上报 触发登录
        addTriggerLogin(LoginSource.knb, 'knb 触发登录');
        // 团App || 外卖App || 点评App
        try {
            await loadKNB();
            window.KNB.login({
                success: function (user) {
                    addLoginSuccess(LoginSource.knb);
                    onLoginSuccess && onLoginSuccess(user);
                },
                fail: function (error) {
                    addLoginError(LoginSource.knb, 'knb登录失败');
                    showToast && showToast('登录失败，请重新登录');
                },
            });
        } catch (error) {
            addLoginError(LoginSource.knb, getErrorMessage(error, 'knb登录，未知错误'));
            showToast && showToast('登录失败，请稍后重试');
        }
    } else if (isMiniProgramTuan || isMiniProgramWaimai) {
        // 团小程序 || 外卖小程序 （团小内复用的外小容器，可同时支持）
        addTriggerLogin(LoginSource.miniProgram, '小程序触发登录');
        try {
            if (oadWX.isMiniProgramLoginReject) {
                throw Error('已存在小程序登录跳转标识');
            } else {
                if (!oadWX.isMiniProgramLoginForce) {
                    // 如果没有客户端强制登录标识或者没有登录状态，强制登录
                    oadWX.miniProgramLogin(window.location.href);
                } else {
                    if (!oadWX.hasWxappLoginStatus()) {
                        // 客户端登陆异常兜底
                        addLoginError(LoginSource.miniProgram, 'wxapp_login_status缺失，没有登录状态');
                        _useH5LoginModalFinally(onLoginSuccess, showH5LoginModal);
                    }
                }
            }
        } catch (error) {
            addLoginError(LoginSource.miniProgram, getErrorMessage(error, '小程序登录，未知错误'));
            _useH5LoginModalFinally(onLoginSuccess, showH5LoginModal);
        }
    } else {
        const source = isInWeixin ? LoginSource.wxCallApp : LoginSource.callApp;
        try {
            // 上报触发
            addTriggerLogin(source, '触发唤起登录');

            // H5兜底
            const dianpingUrl = `dianping://web?url=${encodeURIComponent(window.location.href)}`;
            const iMeiTuanUrl = `imeituan://www.meituan.com/web?url=${encodeURIComponent(window.location.href)}&mt_ret_to=waimai_home`;
            const dpUrl = isDianPingDomain ? dianpingUrl : iMeiTuanUrl;

            deeplink.gotoDp(dpUrl, '', null, {
                deepLinkSuccess: () => {
                    addLoginSuccess(source);
                    setTimeout(() => {
                        _useH5LoginModalFinally(onLoginSuccess, showH5LoginModal);
                    }, 500);
                },
                deepLinkFail: () => {
                    addLoginError(source, 'deeplink 跳转登录失败');
                    _useH5LoginModalFinally(onLoginSuccess, showH5LoginModal);
                },
            });
        } catch (error) {
            addLoginError(source, getErrorMessage(error, '唤起 APP 登录，未知错误'));
            _useH5LoginModalFinally(onLoginSuccess, showH5LoginModal);
        }
    }
};

// 2.1【页面加载完自动触发】团系容器(团App、外卖App)，自动将url中的token写入cookie
if (isInTuanApp || isInWaiMaiApp) {
    try {
        const qsObj = qsParse(window.location.search.substring(1));
        if (qsObj) {
            const token = qsObj.wm_logintoken || qsObj.token || qsObj.mt_c_token || '';
            if (token) {
                addLoginSuccess(LoginSource.app);
                Cookies.set(TOKEN_KEY, token);
            }
        }
    } catch (error) {
        addLoginError(LoginSource.app, error.message || 'App容器初始化token error!');
        console.error('App容器初始化token error!', error);
    }
}

// 2.1【页面加载完自动触发】团系小程序（团、外卖），自动将url中的token写入cookie
if (isMiniProgramTuan || isMiniProgramWaimai) {
    oadWX.miniProgramLoginReady();
}

// 3、【用户触发】切换账号
const oadSwitchLogin = async (props) => {
    const { onLoginSuccess, onLogoutSuccess, showToast = () => {}, showH5LoginModal = () => {} } = props;

    if (isInTuanApp || isInWaiMaiApp || isInDianPingApp) {
        let res = false;
        try {
            await loadKNB();
            res = await _callClientLogout();
        } catch (error) {
            throw error;
        }
        if (res) {
            try {
                onLogoutSuccess && onLogoutSuccess();
            } catch (error) {
                // 回调失败，不影响主流程
            }

            oadLogin(props);
        }
    } else if (isMiniProgramTuan || isMiniProgramWaimai) {
        showToast && showToast('请到小程序【我的】页面进行切换');
    } else {
        if (isDianPingDomain) {
            window.location.href = getDpLogoutUrl();
        } else if (is51PingDomain) {
            window.location.href = get51PingLogoutUrl();
        } else {
            _useH5LoginModalFinally(onLoginSuccess, showH5LoginModal);
        }
    }
};

// 小程序环境强制登录（为了与小程序登录态保持一致）
const oadForceMiniProgramLogin = () => {
    if (isMiniProgramTuan || isMiniProgramWaimai) {
        if (!oadWX.isMiniProgramLoginForce) {
            // 如果没有客户端强制登录标识或者没有登录状态，强制登录
            oadWX.miniProgramLogin(window.location.href);
        } else {
            if (!oadWX.hasWxappLoginStatus()) {
                addLoginError(LoginSource.miniProgram, 'wxapp_force_login参数已存在，但是没有登录状态');
            }
        }
    }
};

export default oadLogin;
export { oadSwitchLogin, oadForceMiniProgramLogin };
